import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Page,
  Card,
  FormLayout,
  TextField,
  Select,
  Checkbox,
  Button,
  Banner,
  BlockStack,
  InlineStack,
  Text,
  Box,
  Divider,
  SkeletonBodyText,
  Modal,
} from '@shopify/polaris';
import { rentalsApi, clinicsApi, assetsApi, doctorsApi, patientsApi } from '@/services';
import { productsApi } from '@/services/products';
import WrapPicker from '@/components/WrapPicker';
import AddOnPicker, { type AddOnSelection } from '@/components/AddOnPicker';
import SearchableSelect from '@/components/SearchableSelect';
import { taxRateForProvince, taxLabelForProvince, isKnownProvince } from '@/utils/tax';
import { addOnsForWrap, wrapAddOnNote } from '@/utils/wrapAddons';
import type { CreateAdminRentalDto } from '@/services/rentals';
import type { Clinic, Doctor, Patient, Rental } from '@/types';
import { orderNumber } from '@/utils/orderNumber';

// Fields returned by the rentals API but not yet on the shared Rental type
// (RentalDetail uses the same cast pattern for these).
type CopySourceRental = Rental & {
  doctorId?: string | null;
  isDemo?: boolean;
  isProbono?: boolean;
  isConsignment?: boolean;
};

const RENTAL_WEEKS_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  label: `${i + 1} week${i + 1 > 1 ? 's' : ''}`,
  value: String(i + 1),
}));

const SHIPPING_ADDRESS_OPTIONS = [
  { label: 'Ship to Clinic', value: 'clinic' },
  { label: 'Ship to Patient', value: 'patient' },
  { label: 'Ship to Custom Address (e.g. UPS store)', value: 'custom' },
];

const EMPTY_CUSTOM_ADDRESS = {
  name: '',
  company: '',
  address1: '',
  address2: '',
  city: '',
  province: '',
  postalCode: '',
  phone: '',
};

// Bilateral one-time fee follows the pricing audience (UAT round-4).
const BILATERAL_FEE_BY_RATE = { retail: 300, wholesale: 180 } as const;

function addDays(dateStr: string, days: number): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function formatCurrency(cents: number): string {
  return `$${cents.toFixed(2)}`;
}

export default function QuickOrder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const copyFromId = searchParams.get('copyFrom');

  const [form, setForm] = useState({
    clinicId: '',
    patientId: '',
    doctorId: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    productSku: '',
    productName: '',
    unitPrice: 0,
    rateType: 'retail' as 'retail' | 'wholesale',
    priceManual: false,
    isBilateral: false,
    wrapSku: '',
    wrapName: '',
    rentalStartDate: '',
    rentalWeeks: 2,
    isDemo: false,
    isProbono: false,
    isConsignment: false,
    taxExempt: false,
    shippingAddressType: 'patient',
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  // Tax exemption is rare and hard to spot once an order is placed, so switching it
  // ON asks for confirmation. Switching it OFF does not — reverting to charging tax
  // is the safe direction.
  const [taxExemptConfirmOpen, setTaxExemptConfirmOpen] = useState(false);
  const [customAddr, setCustomAddr] = useState({ ...EMPTY_CUSTOM_ADDRESS });

  // Doctor search
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);

  // Patient search
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  // Clinics are searched on the server rather than pre-loaded: there are 800+ of them,
  // so any fixed page size hides most of the list with no way to reach the rest.
  const [selectedClinicName, setSelectedClinicName] = useState('');
  const [clinicResultCache, setClinicResultCache] = useState<Clinic[]>([]);
  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
  const searchClinics = useCallback(async (query: string) => {
    const res = await clinicsApi.list({ search: query || undefined, limit: 25, status: 'active' });
    setClinicResultCache(res.data);
    return res.data.map((c) => ({
      label: c.name,
      value: c.id,
      detail: c.email || undefined,
    }));
  }, []);

  // Doctor/patient lookups also run server-side. The previous inline lists showed only
  // the first 5 matches with no indication there were more, so with 45k patients a
  // common surname simply never surfaced the right person.
  const [doctorResultCache, setDoctorResultCache] = useState<Doctor[]>([]);
  const searchDoctors = useCallback(async (query: string) => {
    const res = await doctorsApi.search(query, 25);
    setDoctorResultCache(res);
    return res.map((d) => ({
      label: `${d.firstName} ${d.lastName}`,
      value: d.id,
      detail: d.email || undefined,
    }));
  }, []);

  const [patientResultCache, setPatientResultCache] = useState<Patient[]>([]);
  const searchPatients = useCallback(async (query: string) => {
    const res = await patientsApi.search(query, undefined, 25);
    setPatientResultCache(res);
    return res.map((p) => ({
      label: `${p.firstName} ${p.lastName}`,
      value: p.id,
      detail: p.email || p.phone || undefined,
    }));
  }, []);

  // Fetch inventory summary
  const { data: inventoryData, isLoading: inventoryLoading } = useQuery({
    queryKey: ['inventory-summary'],
    queryFn: () => assetsApi.getInventorySummary(),
  });

  // Fetch wrap products (tagged rental-wrap in Shopify)
  const { data: wrapsData } = useQuery({
    queryKey: ['wrap-products'],
    queryFn: () => productsApi.wraps(),
  });

  // Add-on products (tagged rental-addon) — optional extras on the order
  const { data: addOnProducts } = useQuery({
    queryKey: ['addon-products'],
    queryFn: () => productsApi.addons(),
  });
  const [addOnSelections, setAddOnSelections] = useState<AddOnSelection[]>([]);

  // Fetch full rental product configs — used to auto-price from the product's
  // configured weekly rate (retail vs wholesale) instead of manual entry.
  const { data: productConfigs } = useQuery({
    queryKey: ['rental-product-configs'],
    queryFn: () => productsApi.list(),
  });

  // sku → { retail, wholesale } weekly rate in cents
  const ratesBySku = useMemo(() => {
    const map = new Map<string, { retail: number | null; wholesale: number | null }>();
    if (!productConfigs) return map;
    for (const p of productConfigs) {
      const retail = p.rentalConfig.weeklyRateCents;
      const wholesale = p.rentalConfig.wholesaleWeeklyRateCents;
      for (const v of p.variants) {
        if (v.sku) map.set(v.sku, { retail, wholesale });
      }
    }
    return map;
  }, [productConfigs]);

  // Changing the wrap can invalidate an add-on already chosen — a Flat Pak selected
  // under a knee wrap would otherwise stay on the order while being hidden from view.
  useEffect(() => {
    if (!form.wrapSku || addOnSelections.length === 0) return;
    const allowed = new Set(
      addOnsForWrap(addOnProducts ?? [], form.wrapSku).map((a) => a.shopifyProductId),
    );
    const kept = addOnSelections.filter((sel) => allowed.has(sel.productId));
    if (kept.length !== addOnSelections.length) setAddOnSelections(kept);
  }, [form.wrapSku, addOnProducts, addOnSelections]);

  // Copy-order prefill (?copyFrom=<rentalId>)
  const [copyNotes, setCopyNotes] = useState<string[]>([]);
  const [copyBannerDismissed, setCopyBannerDismissed] = useState(false);
  const copyAppliedRef = useRef(false);
  const copyPatientAppliedRef = useRef(false);
  const copyDoctorAppliedRef = useRef(false);

  const copySourceQuery = useQuery({
    queryKey: ['rental-copy-source', copyFromId],
    queryFn: () => rentalsApi.getById(copyFromId!),
    enabled: !!copyFromId,
    retry: false,
  });
  const copySource = copySourceQuery.data as CopySourceRental | undefined;

  const copyPatientQuery = useQuery({
    queryKey: ['rental-copy-patient', copySource?.patientId],
    queryFn: () => patientsApi.getById(copySource!.patientId!),
    enabled: !!copySource?.patientId,
    retry: false,
  });

  // Fetched by id rather than looked up in a pre-loaded list: clinics are now searched
  // on the server, so there is no full list to check membership against.
  const copyClinicQuery = useQuery({
    queryKey: ['rental-copy-clinic', copySource?.clinicId],
    queryFn: () => clinicsApi.getById(copySource!.clinicId!),
    enabled: !!copySource?.clinicId,
    retry: false,
  });

  const copyDoctorQuery = useQuery({
    queryKey: ['rental-copy-doctor', copySource?.doctorId],
    queryFn: () => doctorsApi.getById(copySource!.doctorId!),
    enabled: !!copySource?.doctorId,
    retry: false,
  });

  // Prefill the form once the source rental, clinic list, and inventory have loaded
  useEffect(() => {
    if (copyAppliedRef.current) return;
    if (!copySource || !inventoryData) return;
    // Wait for the clinic lookup to settle so a valid clinic is not dropped just
    // because its request had not returned yet.
    if (copySource.clinicId && copyClinicQuery.isLoading) return;
    copyAppliedRef.current = true;

    const notes: string[] = [];

    let clinicId = '';
    if (copySource.clinicId) {
      if (copyClinicQuery.data) {
        clinicId = copySource.clinicId;
        setSelectedClinicName(copyClinicQuery.data.name);
      } else {
        notes.push('Original clinic is no longer available — clinic left unset.');
      }
    }

    let productSku = '';
    let productName = '';
    const equipmentItem = copySource.items?.find(
      (item) => (item.itemType ?? 'equipment') === 'equipment',
    );
    if (equipmentItem) {
      const inventoryItem = inventoryData.find((i) => i.sku === equipmentItem.productSku);
      if (inventoryItem) {
        productSku = inventoryItem.sku;
        productName = inventoryItem.productName;
      } else {
        notes.push(
          `Product ${equipmentItem.productSku} is no longer in the product list — product left unset.`,
        );
      }
    }

    const isBilateral =
      (equipmentItem?.isBilateral ?? false) ||
      (copySource.items?.some((item) => item.itemType === 'bilateral_fee') ?? false);

    setForm((prev) => ({
      ...prev,
      clinicId,
      customerName: copySource.patientName ?? '',
      customerEmail: copySource.patientEmail ?? '',
      customerPhone: copySource.patientPhone ?? '',
      productSku,
      productName,
      isBilateral,
      rentalStartDate: prev.rentalStartDate || new Date().toISOString().slice(0, 10),
      isDemo: copySource.isDemo ?? false,
      isProbono: copySource.isProbono ?? false,
      // Only a clinic order can be consignment; the Options checkbox and the
      // order summary both show it, so the $0 pricing is never invisible.
      isConsignment: !!clinicId && (copySource.isConsignment ?? false),
      shippingAddressType: ['clinic', 'patient'].includes(copySource.shippingAddressType ?? '')
        ? copySource.shippingAddressType!
        : prev.shippingAddressType,
      notes: copySource.notes ?? '',
    }));
    setCopyNotes((prev) => [...prev, ...notes]);
  }, [copySource, copyClinicQuery.data, copyClinicQuery.isLoading, inventoryData]);

  // Resolve the copied patient (sets the search-select display)
  useEffect(() => {
    if (copyPatientAppliedRef.current || !copySource?.patientId) return;
    const copiedPatient = copyPatientQuery.data;
    if (copiedPatient) {
      copyPatientAppliedRef.current = true;
      setSelectedPatient(copiedPatient);
      setForm((prev) => ({ ...prev, patientId: copiedPatient.id }));
    } else if (copyPatientQuery.isError) {
      copyPatientAppliedRef.current = true;
      setCopyNotes((prev) => [
        ...prev,
        'Original patient could not be loaded — patient left unset.',
      ]);
    }
  }, [copySource?.patientId, copyPatientQuery.data, copyPatientQuery.isError]);

  // Resolve the copied doctor (sets the search-select display)
  useEffect(() => {
    if (copyDoctorAppliedRef.current || !copySource?.doctorId) return;
    const copiedDoctor = copyDoctorQuery.data;
    if (copiedDoctor) {
      copyDoctorAppliedRef.current = true;
      setSelectedDoctor(copiedDoctor);
      setForm((prev) => ({ ...prev, doctorId: copiedDoctor.id }));
    } else if (copyDoctorQuery.isError) {
      copyDoctorAppliedRef.current = true;
      setCopyNotes((prev) => [
        ...prev,
        'Original referring doctor could not be loaded — doctor left unset.',
      ]);
    }
  }, [copySource?.doctorId, copyDoctorQuery.data, copyDoctorQuery.isError]);

  // Machines = inventory entries whose SKU belongs to a rental-enabled product
  // that isn't a wrap. The raw inventory summary spans the whole Asset table
  // (legacy imports included), so without this filter a single stray SKU makes
  // the form think several machine types are configured and shows a selector.
  const machineInventory = useMemo(() => {
    if (!inventoryData) return inventoryData;
    if (!productConfigs) return inventoryData;
    const wrapSkus = new Set(
      (wrapsData ?? []).flatMap((p) => p.variants.map((v) => v.sku)),
    );
    const rentalSkus = new Set(
      productConfigs
        .filter((p) => p.rentalConfig.isRentalProduct)
        .flatMap((p) => p.variants.map((v) => v.sku)),
    );
    const machines = inventoryData.filter(
      (item) => rentalSkus.has(item.sku) && !wrapSkus.has(item.sku),
    );
    // Safety net: if nothing survives the filter (e.g. metafields missing on
    // staging), fall back to the raw list rather than an empty machine choice.
    return machines.length > 0 ? machines : inventoryData;
  }, [inventoryData, productConfigs, wrapsData]);

  const productOptions = useMemo(() => {
    const base = [{ label: 'Select a product...', value: '' }];
    if (!machineInventory) return base;
    return [
      ...base,
      ...machineInventory.map((item) => ({
        label: `${item.productName} (${item.sku}) — ${item.ready} available`,
        value: item.sku,
      })),
    ];
  }, [machineInventory]);

  // One option per wrap variant (size); wraps ship free with the machine
  const wrapOptions = useMemo(() => {
    const base = [{ label: 'No wrap', value: '' }];
    if (!wrapsData) return base;
    return [
      ...base,
      ...wrapsData.flatMap((product) =>
        product.variants
          .filter((v) => v.sku)
          .map((v) => ({
            label:
              v.title && v.title !== 'Default Title'
                ? `${product.title} — ${v.title}`
                : product.title,
            value: v.sku,
          })),
      ),
    ];
  }, [wrapsData]);

  // Prefill the wrap once the wrap product list has loaded (after wrapOptions
  // is computed — keep this effect below that declaration)
  const copyWrapAppliedRef = useRef(false);
  useEffect(() => {
    if (copyWrapAppliedRef.current) return;
    if (!copySource || !wrapsData) return;
    copyWrapAppliedRef.current = true;

    const wrapItem = copySource.items?.find((item) => item.itemType === 'wrap');
    if (!wrapItem) return;
    const option = wrapOptions.find((o) => o.value === wrapItem.productSku);
    if (option) {
      setForm((prev) => ({ ...prev, wrapSku: option.value, wrapName: option.label }));
    } else {
      setCopyNotes((prev) => [
        ...prev,
        `Wrap ${wrapItem.productSku} is no longer in the wrap list — wrap left unset.`,
      ]);
    }
  }, [copySource, wrapsData, wrapOptions]);

  // The machine is a given on every rental — only the machine carries a serial,
  // the patient/clinic just picks the wrap (Adrian: "the choice is always the
  // wrap"). Auto-select the machine when the catalog has exactly one, so staff
  // never pick it manually. Only serial-tracked machines appear in inventory
  // (wraps have no serial), so a single entry here is unambiguous.
  useEffect(() => {
    if (form.productSku) return;
    if (machineInventory && machineInventory.length === 1) {
      const machine = machineInventory[0];
      setForm((prev) => ({
        ...prev,
        productSku: machine.sku,
        productName: machine.productName,
      }));
    }
  }, [machineInventory, form.productSku]);

  // Derived calculations
  const endDate = useMemo(
    () => addDays(form.rentalStartDate, form.rentalWeeks * 7),
    [form.rentalStartDate, form.rentalWeeks],
  );

  // Demo / pro bono / consignment orders carry no charge. Everything (price,
  // bilateral fee, tax) collapses to $0 so the summary can't show a phantom total.
  const isFreeOrder = form.isDemo || form.isProbono || form.isConsignment;

  // The configured weekly rate for the selected product + audience (cents)
  const selectedWeeklyRateCents = useMemo(() => {
    const rates = ratesBySku.get(form.productSku);
    if (!rates) return null;
    return form.rateType === 'wholesale' ? rates.wholesale : rates.retail;
  }, [ratesBySku, form.productSku, form.rateType]);

  // Auto-fill the unit price from rate × weeks unless the user is editing it
  // manually or this is a no-charge order.
  useEffect(() => {
    if (form.priceManual || isFreeOrder) return;
    if (selectedWeeklyRateCents == null) return;
    const price = (selectedWeeklyRateCents / 100) * form.rentalWeeks;
    setForm((prev) => (prev.unitPrice === price ? prev : { ...prev, unitPrice: price }));
  }, [selectedWeeklyRateCents, form.rentalWeeks, form.priceManual, isFreeOrder]);

  // Default to wholesale pricing when a clinic is selected, retail otherwise
  // (user can still override the rate type).
  const clinicId = form.clinicId;
  useEffect(() => {
    setForm((prev) => ({ ...prev, rateType: clinicId ? 'wholesale' : 'retail' }));
  }, [clinicId]);

  const effectiveUnitPrice = isFreeOrder ? 0 : form.unitPrice;
  // Tax follows the shipping destination — same precedence the server uses, so the
  // preview matches what actually gets stored.
  const destinationProvince = (() => {
    if (form.shippingAddressType === 'custom') return customAddr.province;
    if (form.shippingAddressType === 'patient' && selectedPatient?.province) {
      return selectedPatient.province;
    }
    const clinicAddr = (selectedClinic?.shippingAddress ?? selectedClinic?.billingAddress) as
      | { province?: string }
      | undefined;
    if (clinicAddr?.province) return clinicAddr.province;
    return selectedPatient?.province ?? '';
  })();
  const taxRate =
    isFreeOrder || form.taxExempt ? 0 : taxRateForProvince(destinationProvince);
  const bilateralFeeDollars = BILATERAL_FEE_BY_RATE[form.rateType as 'retail' | 'wholesale'] ?? 300;
  const bilateralFee = !isFreeOrder && form.isBilateral ? bilateralFeeDollars : 0;
  const addOnsTotal = isFreeOrder
    ? 0
    : addOnSelections.reduce((sum, a) => sum + a.quantity * a.unitPrice, 0);
  const subtotal = effectiveUnitPrice + bilateralFee + addOnsTotal;
  const taxAmount = subtotal * taxRate;
  const total = subtotal + taxAmount;

  // Mutation
  const mutation = useMutation({
    mutationFn: (dto: CreateAdminRentalDto) => rentalsApi.createAdmin(dto),
    onSuccess: (rental) => {
      navigate(`/rentals/${rental.id}`);
    },
  });

  function setField<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }

  function setCustomField<K extends keyof typeof customAddr>(
    key: K,
    value: (typeof customAddr)[K],
  ) {
    setCustomAddr((prev) => ({ ...prev, [key]: value }));
    const errorKey = `custom_${key}`;
    if (errors[errorKey]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[errorKey];
        return next;
      });
    }
  }

  function handleProductChange(sku: string) {
    setField('productSku', sku);
    if (!sku) {
      setField('productName', '');
      return;
    }
    const item = inventoryData?.find((i) => i.sku === sku);
    if (item) {
      setField('productName', item.productName);
    }
  }

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!form.customerName.trim()) newErrors.customerName = 'Customer name is required';
    if (!form.wrapSku) newErrors.wrapSku = 'Select a wrap';
    if (!form.productSku)
      newErrors.productSku = 'No machine available in inventory to assign';
    if (!form.rentalStartDate) newErrors.rentalStartDate = 'Start date is required';
    if (!isFreeOrder && form.unitPrice <= 0)
      newErrors.unitPrice =
        'Set a weekly rate on this product, or enter the price manually';
    if (form.shippingAddressType === 'custom') {
      if (!customAddr.address1.trim()) newErrors.custom_address1 = 'Street address is required';
      if (!customAddr.city.trim()) newErrors.custom_city = 'City is required';
      if (!customAddr.province.trim()) newErrors.custom_province = 'Province is required';
      if (!customAddr.postalCode.trim()) newErrors.custom_postalCode = 'Postal code is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;

    const dto: CreateAdminRentalDto = {
      clinicId: form.clinicId || undefined,
      patientId: form.patientId || undefined,
      doctorId: form.doctorId || undefined,
      customerName: form.customerName || undefined,
      customerEmail: form.customerEmail || undefined,
      customerPhone: form.customerPhone || undefined,
      productSku: form.productSku,
      productName: form.productName,
      unitPrice: isFreeOrder ? 0 : form.unitPrice,
      isBilateral: form.isBilateral,
      rateType: form.rateType,
      wrapSku: form.wrapSku || undefined,
      wrapName: form.wrapName || undefined,
      addOns: addOnSelections.length
        ? addOnSelections.map((a) => ({
            sku: a.sku,
            name: a.name,
            quantity: a.quantity,
            unitPrice: a.unitPrice,
          }))
        : undefined,
      rentalStartDate: form.rentalStartDate,
      rentalWeeks: form.rentalWeeks,
      isDemo: form.isDemo,
      isProbono: form.isProbono,
      isConsignment: form.isConsignment,
      taxExempt: form.taxExempt,
      shippingAddressType: form.shippingAddressType,
      customShippingAddress:
        form.shippingAddressType === 'custom'
          ? {
              name: customAddr.name || form.customerName || undefined,
              company: customAddr.company || undefined,
              address1: customAddr.address1,
              address2: customAddr.address2 || undefined,
              city: customAddr.city,
              province: customAddr.province,
              postalCode: customAddr.postalCode,
              country: 'CA',
              phone: customAddr.phone || form.customerPhone || undefined,
            }
          : undefined,
      notes: form.notes || undefined,
    };

    mutation.mutate(dto);
  }

  return (
    <Page
      title="Quick Order Create"
      backAction={{ content: 'Rentals', onAction: () => navigate('/rentals') }}
    >
      <BlockStack gap="400">
        {copyFromId && copySourceQuery.isError && (
          <Banner tone="warning" title="Could not copy order">
            <p>
              The order you tried to copy could not be loaded. Fill in the form manually, or go
              back and try again.
            </p>
          </Banner>
        )}

        {copySource && !copyBannerDismissed && (
          <Banner
            tone="info"
            title={`Copying order #${orderNumber(copySource)} — review details before placing`}
            onDismiss={() => setCopyBannerDismissed(true)}
          >
            <BlockStack gap="100">
              <Text as="p">
                Clinic, patient, doctor, customer, product, and order options have been
                pre-filled. Serials, dates, and pricing are not copied.
              </Text>
              {copyNotes.map((note) => (
                <Text as="p" key={note} tone="subdued">
                  {note}
                </Text>
              ))}
            </BlockStack>
          </Banner>
        )}

        {/* Section 1: Clinic */}
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Clinic
            </Text>
            <FormLayout>
              <SearchableSelect
                label="Clinic (optional)"
                value={form.clinicId}
                selectedLabel={selectedClinicName}
                onChange={(v, option) => {
                  setField('clinicId', v);
                  // Consignment needs a clinic to hold the stock.
                  if (!v) setField('isConsignment', false);
                  setSelectedClinicName(option?.label ?? '');
                  setSelectedClinic(clinicResultCache.find((c) => c.id === v) ?? null);
                }}
                onSearch={searchClinics}
                emptyOption={{ label: 'No clinic (Direct Order)', value: '' }}
                placeholder="Type a clinic name…"
                helpText="Search by clinic name or email"
              />
            </FormLayout>
          </BlockStack>
        </Card>

        {/* Section 2: Doctor & Patient */}
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Doctor &amp; Patient
            </Text>
            <FormLayout>
              <FormLayout.Group>
                <BlockStack gap="200">
                  <SearchableSelect
                    label="Referring Doctor"
                    value={form.doctorId}
                    selectedLabel={
                      selectedDoctor ? `${selectedDoctor.firstName} ${selectedDoctor.lastName}` : ''
                    }
                    onChange={(v) => {
                      setField('doctorId', v);
                      setSelectedDoctor(v ? (doctorResultCache.find((d) => d.id === v) ?? null) : null);
                    }}
                    onSearch={searchDoctors}
                    minChars={2}
                    placeholder="Type a doctor name…"
                    helpText="Search by first name, last name or email"
                  />
                </BlockStack>
                <BlockStack gap="200">
                  <SearchableSelect
                    label="Patient"
                    value={form.patientId}
                    selectedLabel={
                      selectedPatient
                        ? `${selectedPatient.firstName} ${selectedPatient.lastName}`
                        : ''
                    }
                    onChange={(v, option) => {
                      setField('patientId', v);
                      if (!v) {
                        setSelectedPatient(null);
                        return;
                      }
                      const pat = patientResultCache.find((x: Patient) => x.id === v);
                      setSelectedPatient(pat ?? ({ id: v } as Patient));
                      if (pat) {
                        setField('customerName', `${pat.firstName} ${pat.lastName}`);
                        if (pat.email) setField('customerEmail', pat.email);
                        if (pat.phone) setField('customerPhone', pat.phone);
                      } else if (option) {
                        setField('customerName', option.label);
                      }
                    }}
                    onSearch={searchPatients}
                    minChars={2}
                    placeholder="Type a patient name…"
                    helpText="Search by first name, last name, email or phone"
                  />
                </BlockStack>
              </FormLayout.Group>
              <FormLayout.Group>
                <TextField
                  label="Customer Name"
                  value={form.customerName}
                  onChange={(v) => setField('customerName', v)}
                  autoComplete="name"
                  error={errors.customerName}
                />
                <TextField
                  label="Customer Email"
                  type="email"
                  value={form.customerEmail}
                  onChange={(v) => setField('customerEmail', v)}
                  autoComplete="email"
                />
              </FormLayout.Group>
              <TextField
                label="Customer Phone"
                type="tel"
                value={form.customerPhone}
                onChange={(v) => setField('customerPhone', v)}
                autoComplete="tel"
              />
            </FormLayout>
          </BlockStack>
        </Card>

        {/* Section 3: Wrap (the machine is a given — only the wrap is chosen) */}
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Wrap
            </Text>
            {inventoryLoading ? (
              <SkeletonBodyText lines={2} />
            ) : (
              <FormLayout>
                <BlockStack gap="200">
                  <Text as="p" tone="subdued">
                    The body-part wrap the patient needs. The Game Ready machine is
                    included and its serial is assigned at dispatch. Bilateral orders
                    ship two of the same wrap.
                  </Text>
                  <WrapPicker
                    wraps={wrapsData ?? []}
                    selectedSku={form.wrapSku}
                    allowNone
                    onChange={(sel) => {
                      setField('wrapSku', sel?.sku ?? '');
                      setField('wrapName', sel?.label ?? '');
                    }}
                    error={errors.wrapSku}
                  />
                </BlockStack>
                {/* The machine is implied on every rental. Show it read-only when
                    the catalog has a single unit; only fall back to a selector if
                    more than one machine type is configured. */}
                {machineInventory && machineInventory.length > 1 ? (
                  <Select
                    label="Machine"
                    options={productOptions}
                    value={form.productSku}
                    onChange={handleProductChange}
                    error={errors.productSku}
                    helpText="More than one machine type is configured — pick the one that ships."
                  />
                ) : (
                  <TextField
                    label="Machine (included)"
                    value={form.productName || form.productSku || 'Game Ready unit'}
                    disabled
                    autoComplete="off"
                    error={errors.productSku}
                    helpText="Ships with every rental; serial assigned at dispatch."
                  />
                )}
                <Checkbox
                  label={`Bilateral ($${bilateralFeeDollars} CAD one-time charge)`}
                  helpText="Adds a one-time bilateral fee and ships two of the same wrap."
                  checked={form.isBilateral}
                  onChange={(v) => setField('isBilateral', v)}
                />
                {(addOnProducts?.length ?? 0) > 0 && (
                  <BlockStack gap="200">
                    <Text as="h3" variant="headingSm">Add-ons</Text>
                    {wrapAddOnNote(form.wrapSku) && (
                      <Text as="p" variant="bodySm" tone="subdued">
                        {wrapAddOnNote(form.wrapSku)}
                      </Text>
                    )}
                    <AddOnPicker
                      addons={addOnsForWrap(addOnProducts ?? [], form.wrapSku)}
                      selections={addOnSelections}
                      onChange={setAddOnSelections}
                      rateType={form.rateType as 'retail' | 'wholesale'}
                    />
                  </BlockStack>
                )}
              </FormLayout>
            )}
          </BlockStack>
        </Card>

        {/* Section 4: Dates & Pricing */}
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Dates &amp; Pricing
            </Text>
            <FormLayout>
              <FormLayout.Group>
                <TextField
                  label="Rental Start Date"
                  type="date"
                  value={form.rentalStartDate}
                  onChange={(v) => setField('rentalStartDate', v)}
                  error={errors.rentalStartDate}
                  autoComplete="off"
                />
                <Select
                  label="Rental Duration"
                  options={RENTAL_WEEKS_OPTIONS}
                  value={String(form.rentalWeeks)}
                  onChange={(v) => setField('rentalWeeks', parseInt(v, 10))}
                />
              </FormLayout.Group>
              {isFreeOrder ? (
                <Banner tone="info">
                  No charge —{' '}
                  {form.isProbono
                    ? 'pro bono'
                    : form.isDemo
                      ? 'demo/loaner'
                      : 'consignment'}{' '}
                  orders are billed at $0.
                </Banner>
              ) : (
                <>
                  <FormLayout.Group>
                    <Select
                      label="Pricing"
                      options={[
                        { label: 'Retail (direct)', value: 'retail' },
                        { label: 'Wholesale (clinic)', value: 'wholesale' },
                      ]}
                      value={form.rateType}
                      onChange={(v) =>
                        setField('rateType', v as 'retail' | 'wholesale')
                      }
                      helpText={
                        selectedWeeklyRateCents != null
                          ? `${formatCurrency(selectedWeeklyRateCents / 100)}/wk × ${form.rentalWeeks} wk`
                          : 'No weekly rate set on this product — enter manually'
                      }
                    />
                    <TextField
                      label="Unit Price"
                      type="number"
                      prefix="$"
                      value={form.unitPrice > 0 ? String(form.unitPrice) : ''}
                      onChange={(v) =>
                        setField('unitPrice', parseFloat(v) || 0)
                      }
                      error={errors.unitPrice}
                      disabled={!form.priceManual}
                      helpText={
                        form.priceManual
                          ? 'Manual override'
                          : 'Auto-calculated from the weekly rate'
                      }
                      autoComplete="off"
                    />
                  </FormLayout.Group>
                  <Checkbox
                    label="Set price manually"
                    checked={form.priceManual}
                    onChange={(v) => setField('priceManual', v)}
                  />
                </>
              )}
            </FormLayout>

            {(form.rentalStartDate || form.unitPrice > 0 || isFreeOrder) && (
              <>
                <Divider />
                <BlockStack gap="200">
                  {endDate && (
                    <InlineStack align="space-between">
                      <Text as="span" tone="subdued">End Date</Text>
                      <Text as="span">{endDate}</Text>
                    </InlineStack>
                  )}
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Rental</Text>
                    <Text as="span">{formatCurrency(effectiveUnitPrice)}</Text>
                  </InlineStack>
                  {form.isBilateral && !isFreeOrder && (
                    <InlineStack align="space-between">
                      <Text as="span" tone="subdued">Bilateral fee (one-time)</Text>
                      <Text as="span">{formatCurrency(bilateralFee)}</Text>
                    </InlineStack>
                  )}
                  {addOnsTotal > 0 && (
                    <InlineStack align="space-between">
                      <Text as="span" tone="subdued">Add-ons</Text>
                      <Text as="span">{formatCurrency(addOnsTotal)}</Text>
                    </InlineStack>
                  )}
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">Subtotal</Text>
                    <Text as="span">{formatCurrency(subtotal)}</Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" tone="subdued">
                      Tax ({isFreeOrder ? '0% — no charge' : '13% HST'})
                    </Text>
                    <Text as="span">{formatCurrency(taxAmount)}</Text>
                  </InlineStack>
                  <InlineStack align="space-between">
                    <Text as="span" variant="bodyMd" fontWeight="semibold">Total</Text>
                    <Text as="span" variant="bodyMd" fontWeight="semibold">{formatCurrency(total)}</Text>
                  </InlineStack>
                </BlockStack>
              </>
            )}
          </BlockStack>
        </Card>

        {/* Section 5: Options */}
        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Options
            </Text>
            <FormLayout>
              <InlineStack gap="400" wrap>
                <Checkbox
                  label="Demo/Loaner"
                  checked={form.isDemo}
                  onChange={(v) => setField('isDemo', v)}
                  helpText="No tax applied"
                />
                <Checkbox
                  label="Pro Bono"
                  checked={form.isProbono}
                  onChange={(v) => setField('isProbono', v)}
                  helpText="No tax applied"
                />
                <Checkbox
                  label="Consignment"
                  checked={form.isConsignment}
                  disabled={!form.clinicId}
                  onChange={(v) => setField('isConsignment', v)}
                  helpText={
                    form.clinicId
                      ? 'Unit stocked at the clinic — no charge'
                      : 'Choose a clinic first'
                  }
                />
                <Checkbox
                  label="Tax exempt"
                  checked={form.taxExempt}
                  disabled={isFreeOrder}
                  onChange={(v) => {
                    if (v) setTaxExemptConfirmOpen(true);
                    else setField('taxExempt', false);
                  }}
                  helpText={
                    isFreeOrder
                      ? 'Already untaxed (demo/pro bono)'
                      : 'Removes HST from this order'
                  }
                />
              </InlineStack>
              <Select
                label="Shipping Address"
                options={SHIPPING_ADDRESS_OPTIONS}
                value={form.shippingAddressType}
                onChange={(v) => setField('shippingAddressType', v)}
              />
              {form.shippingAddressType === 'custom' && (
                <>
                  <FormLayout.Group>
                    <TextField
                      label="Recipient Name"
                      value={customAddr.name}
                      onChange={(v) => setCustomField('name', v)}
                      placeholder="Defaults to customer name"
                      autoComplete="off"
                    />
                    <TextField
                      label="Company / Location"
                      value={customAddr.company}
                      onChange={(v) => setCustomField('company', v)}
                      placeholder="e.g. The UPS Store #123"
                      autoComplete="off"
                    />
                  </FormLayout.Group>
                  <TextField
                    label="Street Address"
                    value={customAddr.address1}
                    onChange={(v) => setCustomField('address1', v)}
                    error={errors.custom_address1}
                    autoComplete="off"
                  />
                  <TextField
                    label="Apt / Suite / Unit (optional)"
                    value={customAddr.address2}
                    onChange={(v) => setCustomField('address2', v)}
                    autoComplete="off"
                  />
                  <FormLayout.Group>
                    <TextField
                      label="City"
                      value={customAddr.city}
                      onChange={(v) => setCustomField('city', v)}
                      error={errors.custom_city}
                      autoComplete="off"
                    />
                    <TextField
                      label="Province"
                      value={customAddr.province}
                      onChange={(v) => setCustomField('province', v)}
                      error={errors.custom_province}
                      placeholder="ON"
                      autoComplete="off"
                    />
                    <TextField
                      label="Postal Code"
                      value={customAddr.postalCode}
                      onChange={(v) => setCustomField('postalCode', v)}
                      error={errors.custom_postalCode}
                      autoComplete="off"
                    />
                  </FormLayout.Group>
                  <TextField
                    label="Phone (optional)"
                    type="tel"
                    value={customAddr.phone}
                    onChange={(v) => setCustomField('phone', v)}
                    placeholder="Defaults to customer phone"
                    autoComplete="off"
                  />
                </>
              )}
              <TextField
                label="Notes"
                value={form.notes}
                onChange={(v) => setField('notes', v)}
                multiline={4}
                autoComplete="off"
                placeholder="Internal notes about this order..."
              />
            </FormLayout>
          </BlockStack>
        </Card>

        {/* Section 6: Review & Submit */}
        <Card>
          <BlockStack gap="400">
            <Text as="h2" variant="headingMd">
              Review &amp; Submit
            </Text>

            {mutation.isError && (
              <Banner tone="critical" title="Failed to create order">
                <p>
                  {mutation.error instanceof Error
                    ? mutation.error.message
                    : 'An unexpected error occurred. Please try again.'}
                </p>
              </Banner>
            )}

            {form.customerName || form.productSku ? (
              <Banner tone="info" title="Order Summary">
                <BlockStack gap="100">
                  {form.customerName && (
                    <Text as="p">
                      <strong>Customer:</strong> {form.customerName}
                      {form.customerEmail ? ` — ${form.customerEmail}` : ''}
                    </Text>
                  )}
                  {form.wrapSku && (
                    <Text as="p">
                      <strong>Wrap:</strong> {form.wrapName || form.wrapSku}
                      {form.isBilateral ? ' × 2 (bilateral)' : ''}
                    </Text>
                  )}
                  {form.productSku && (
                    <Text as="p">
                      <strong>Machine:</strong> {form.productName || form.productSku}{' '}
                      (serial assigned at dispatch)
                    </Text>
                  )}
                  {form.rentalStartDate && (
                    <Text as="p">
                      <strong>Dates:</strong> {form.rentalStartDate}
                      {endDate ? ` → ${endDate}` : ''} ({form.rentalWeeks} week{form.rentalWeeks > 1 ? 's' : ''})
                    </Text>
                  )}
                  {form.unitPrice > 0 && (
                    <Text as="p">
                      <strong>Total:</strong> {formatCurrency(total)}
                      {form.isDemo
                        ? ' (Demo — no tax)'
                        : form.isProbono
                          ? ' (Pro Bono — no tax)'
                          : form.isConsignment
                          ? ' (Consignment — no charge)'
                          : form.taxExempt
                          ? ' (Tax exempt — no tax)'
                          : ` (incl. ${taxLabelForProvince(destinationProvince)}${
                              isKnownProvince(destinationProvince) ? '' : ' — destination unknown'
                            })`}
                    </Text>
                  )}
                  {form.clinicId && (
                    <Text as="p">
                      <strong>Clinic:</strong> {selectedClinicName || form.clinicId}
                    </Text>
                  )}
                  <Text as="p">
                    <strong>Ship to:</strong>{' '}
                    {form.shippingAddressType === 'clinic'
                      ? 'Clinic address'
                      : form.shippingAddressType === 'custom'
                        ? customAddr.address1
                          ? `${customAddr.company ? `${customAddr.company}, ` : ''}${customAddr.address1}, ${customAddr.city} ${customAddr.postalCode}`
                          : 'Custom address (fill in above)'
                        : 'Patient address'}
                  </Text>
                </BlockStack>
              </Banner>
            ) : (
              <Box paddingBlock="200">
                <Text as="p" tone="subdued">
                  Fill in the form above to see a summary here.
                </Text>
              </Box>
            )}

            <InlineStack align="end">
              <Button
                variant="primary"
                loading={mutation.isPending}
                disabled={mutation.isPending}
                onClick={handleSubmit}
              >
                Create Order
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>
      </BlockStack>

      {/* Tax exemption confirmation. Rare and easy to miss once the order exists, so
          turning it ON asks first; turning it OFF does not — reverting to charging
          tax is the safe direction. */}
      <Modal
        open={taxExemptConfirmOpen}
        onClose={() => setTaxExemptConfirmOpen(false)}
        title="Make this order tax exempt?"
        primaryAction={{
          content: 'Yes, exempt this order',
          destructive: true,
          onAction: () => {
            setField('taxExempt', true);
            setTaxExemptConfirmOpen(false);
          },
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: () => setTaxExemptConfirmOpen(false) },
        ]}
      >
        <Modal.Section>
          <Text as="p">
            No HST will be charged on this order. Only do this where the customer holds
            a valid exemption — it affects what is invoiced and reported.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
